import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkAddons from "../../services/Order/useBulkAddons";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";
import useBulkPackages from "../../services/Order/useBulkPackages";

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

const EMPTY_ADDON = {
  addon_id: "",
  restaurant_id: "",
  addon_name: "",
  linked_item_id: "",
  linked_variant_id: "",
  price_type: "",
  price: "",
  status: "ACTIVE",
};

const EMPTY_PACKAGE_ADDON = {
  package_id: "",
  addon_id: "",
  new_package_id: "",
  new_addon_id: "",
};

export function BulkAddons() {
  const {
    addonsLoading,
    addonSaving,
    addonDeletingId,
    getAddons,
    insertAddon,
    updateAddon,
    deleteAddon,
  } = useBulkAddons();
  const { getRestaurants, getItems, getItemVariants } = useBulkCatalogue();

  const [rows, setRows] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [items, setItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [form, setForm] = useState(EMPTY_ADDON);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const addonRows = await getAddons("");
    setRows(Array.isArray(addonRows) ? addonRows : []);
  };

  const ensureAddonOptions = async () => {
    const [restaurantRowsRaw, itemRowsRaw, variantRowsRaw] = await Promise.all([
      restaurants.length ? Promise.resolve(restaurants) : getRestaurants(""),
      items.length ? Promise.resolve(items) : getItems("", ""),
      variants.length ? Promise.resolve(variants) : getItemVariants(""),
    ]);
    const restaurantRows = Array.isArray(restaurantRowsRaw)
      ? restaurantRowsRaw
      : [];
    const itemRows = Array.isArray(itemRowsRaw) ? itemRowsRaw : [];
    const variantRows = Array.isArray(variantRowsRaw) ? variantRowsRaw : [];
    setRestaurants(restaurantRows);
    setItems(itemRows);
    setVariants(variantRows);
    return { restaurantRows, itemRows, variantRows };
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (row) => {
    await ensureAddonOptions();
    setForm({
      addon_id: String(row?.addon_id ?? ""),
      restaurant_id: String(row?.restaurant_id ?? ""),
      addon_name: String(row?.addon_name ?? ""),
      linked_item_id: String(row?.linked_item_id ?? ""),
      linked_variant_id: String(row?.linked_variant_id ?? ""),
      price_type: String(row?.price_type ?? ""),
      price: String(row?.price ?? ""),
      status: String(row?.status ?? "ACTIVE"),
    });
    setModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const saved = form.addon_id
      ? await updateAddon(form)
      : await insertAddon(form);
    if (saved) {
      setModal(false);
      setForm(EMPTY_ADDON);
      load();
    }
  };

  const getName = (list, idKey, nameKey, id, label) =>
    list.find((row) => String(row?.[idKey] ?? row?.id) === String(id))?.[
      nameKey
    ] || `${label} #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Addons"
        subtitle="Manage restaurant addons, linked items, variants and prices."
        loading={addonsLoading}
        onRefresh={load}
        onAdd={async () => {
          const { restaurantRows } = await ensureAddonOptions();
          setForm({
            ...EMPTY_ADDON,
            restaurant_id: String(
              restaurantRows?.[0]?.restaurant_id ??
                restaurantRows?.[0]?.id ??
                "",
            ),
          });
          setModal(true);
        }}
        add="Add Addon"
      />

      <Rows
        loading={addonsLoading}
        empty="No addons found."
        rows={rows}
        render={(row, index) => {
          const id = String(row?.addon_id ?? "");
          return (
            <div
              key={id || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">
                  {row?.addon_name || "Unnamed Addon"}
                </b>
                <p className="text-[11px] text-slate-500">
                  ID #{id || "—"} ·{" "}
                  {getName(
                    restaurants,
                    "restaurant_id",
                    "restaurant_name",
                    row?.restaurant_id,
                    "Restaurant",
                  )}{" "}
                  · Item{" "}
                  {row?.linked_item_id
                    ? getName(
                        items,
                        "item_id",
                        "item_name",
                        row.linked_item_id,
                        "Item",
                      )
                    : "—"}{" "}
                  · Variant{" "}
                  {row?.linked_variant_id
                    ? getName(
                        variants,
                        "variant_id",
                        "variant_name",
                        row.linked_variant_id,
                        "Variant",
                      )
                    : "—"}{" "}
                  ·{row?.price_type || "—"} ₹{row?.price || "0"} ·{" "}
                  {row?.status || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={addonDeletingId === id}
                onClick={async () => {
                  if (await deleteAddon(id)) load();
                }}
              >
                {addonDeletingId === id ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={form.addon_id ? "Edit Addon" : "Add Addon"}
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label="Restaurant"
                value={form.restaurant_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, restaurant_id: value }))
                }
              >
                <option value="">Select Restaurant</option>
                {restaurants.map((row) => {
                  const id = row?.restaurant_id ?? row?.id;
                  return (
                    <option key={id} value={id}>
                      {row?.restaurant_name || `Restaurant #${id}`}
                    </option>
                  );
                })}
              </Select>

              <Input
                label="Addon Name"
                name="addon_name"
                form={form}
                setForm={setForm}
              />

              <Select
                label="Linked Item"
                value={form.linked_item_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, linked_item_id: value }))
                }
              >
                <option value="">Select Item</option>
                {items.map((row) => (
                  <option key={row?.item_id} value={row?.item_id}>
                    {row?.item_name || `Item #${row?.item_id}`}
                  </option>
                ))}
              </Select>

              <Select
                label="Linked Variant"
                value={form.linked_variant_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, linked_variant_id: value }))
                }
              >
                <option value="">Select Variant</option>
                {variants.map((row) => (
                  <option key={row?.variant_id} value={row?.variant_id}>
                    {row?.variant_name || `Variant #${row?.variant_id}`}
                  </option>
                ))}
              </Select>

              <Input
                label="Price Type"
                name="price_type"
                form={form}
                setForm={setForm}
              />
              <Input
                label="Price"
                name="price"
                type="number"
                form={form}
                setForm={setForm}
              />

              <Select
                label="Status"
                value={form.status}
                set={(value) => setForm((prev) => ({ ...prev, status: value }))}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Select>
            </div>
            <Footer
              saving={addonSaving}
              edit={!!form.addon_id}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkPackageAddons() {
  const {
    packageAddonsLoading,
    packageAddonSaving,
    packageAddonDeletingKey,
    getAddons,
    getPackageAddons,
    insertPackageAddon,
    updatePackageAddon,
    deletePackageAddon,
  } = useBulkAddons();
  const { getPackages } = useBulkPackages();

  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [form, setForm] = useState(EMPTY_PACKAGE_ADDON);
  const [editing, setEditing] = useState(false);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const mappingRows = await getPackageAddons("", "");
    setRows(Array.isArray(mappingRows) ? mappingRows : []);
  };

  const ensurePackageAddonOptions = async () => {
    const [packageRowsRaw, addonRowsRaw] = await Promise.all([
      packages.length ? Promise.resolve(packages) : getPackages(""),
      addons.length ? Promise.resolve(addons) : getAddons(""),
    ]);
    const packageRows = Array.isArray(packageRowsRaw) ? packageRowsRaw : [];
    const addonRows = Array.isArray(addonRowsRaw) ? addonRowsRaw : [];
    setPackages(packageRows);
    setAddons(addonRows);
    return { packageRows, addonRows };
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (row) => {
    await ensurePackageAddonOptions();
    const packageId = String(row?.package_id ?? "");
    const addonId = String(row?.addon_id ?? "");
    setForm({
      package_id: packageId,
      addon_id: addonId,
      new_package_id: packageId,
      new_addon_id: addonId,
    });
    setEditing(true);
    setModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const saved = editing
      ? await updatePackageAddon(form)
      : await insertPackageAddon(form);

    if (saved) {
      setModal(false);
      setEditing(false);
      setForm(EMPTY_PACKAGE_ADDON);
      load();
    }
  };

  const packageName = (id) =>
    packages.find((row) => String(row?.package_id) === String(id))
      ?.package_name || `Package #${id || "—"}`;

  const addonName = (id) =>
    addons.find((row) => String(row?.addon_id) === String(id))?.addon_name ||
    `Addon #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Package Addons"
        subtitle="Map addons with packages."
        loading={packageAddonsLoading}
        onRefresh={load}
        onAdd={async () => {
          const { packageRows, addonRows } = await ensurePackageAddonOptions();
          setForm({
            ...EMPTY_PACKAGE_ADDON,
            package_id: String(packageRows?.[0]?.package_id ?? ""),
            addon_id: String(addonRows?.[0]?.addon_id ?? ""),
          });
          setEditing(false);
          setModal(true);
        }}
        add="Add Mapping"
      />

      <Rows
        loading={packageAddonsLoading}
        empty="No package addons found."
        rows={rows}
        render={(row, index) => {
          const packageId = String(row?.package_id ?? "");
          const addonId = String(row?.addon_id ?? "");
          const key = `${packageId}:${addonId}`;

          return (
            <div
              key={key || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">
                  {packageName(packageId)}
                </b>
                <p className="text-[11px] text-slate-500">
                  {addonName(addonId)} · Package #{packageId || "—"} · Addon #
                  {addonId || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={packageAddonDeletingKey === key}
                onClick={async () => {
                  if (await deletePackageAddon(packageId, addonId)) load();
                }}
              >
                {packageAddonDeletingKey === key ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={editing ? "Edit Package Addon" : "Add Package Addon"}
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label={editing ? "Current Package" : "Package"}
                value={form.package_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, package_id: value }))
                }
                disabled={editing}
              >
                <option value="">Select Package</option>
                {packages.map((row) => (
                  <option key={row?.package_id} value={row?.package_id}>
                    {row?.package_name || `Package #${row?.package_id}`}
                  </option>
                ))}
              </Select>

              <Select
                label={editing ? "Current Addon" : "Addon"}
                value={form.addon_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, addon_id: value }))
                }
                disabled={editing}
              >
                <option value="">Select Addon</option>
                {addons.map((row) => (
                  <option key={row?.addon_id} value={row?.addon_id}>
                    {row?.addon_name || `Addon #${row?.addon_id}`}
                  </option>
                ))}
              </Select>

              {editing && (
                <>
                  <Select
                    label="New Package"
                    value={form.new_package_id}
                    set={(value) =>
                      setForm((prev) => ({ ...prev, new_package_id: value }))
                    }
                  >
                    <option value="">Select New Package</option>
                    {packages.map((row) => (
                      <option key={row?.package_id} value={row?.package_id}>
                        {row?.package_name || `Package #${row?.package_id}`}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="New Addon"
                    value={form.new_addon_id}
                    set={(value) =>
                      setForm((prev) => ({ ...prev, new_addon_id: value }))
                    }
                  >
                    <option value="">Select New Addon</option>
                    {addons.map((row) => (
                      <option key={row?.addon_id} value={row?.addon_id}>
                        {row?.addon_name || `Addon #${row?.addon_id}`}
                      </option>
                    ))}
                  </Select>
                </>
              )}
            </div>
            <Footer
              saving={packageAddonSaving}
              edit={editing}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({ title, subtitle, loading, onRefresh, onAdd, add }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />{" "}
          Refresh
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} /> {add}
        </button>
      </div>
    </div>
  );
}

function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;
  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}

function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={close}>
            <X size={17} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Select({ label, value, set, children, disabled = false }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => set(event.target.value)}
        disabled={disabled}
        className={field}
      >
        {children}
      </select>
    </label>
  );
}

function Input({ label, name, form, setForm, type = "text" }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type={type}
        value={form[name]}
        onChange={(event) =>
          setForm((prev) => ({ ...prev, [name]: event.target.value }))
        }
        className={field}
      />
    </label>
  );
}

function Icon({ children, danger, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${danger ? "border-rose-100 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500"}`}
    >
      {children}
    </button>
  );
}

function Footer({ saving, edit, close }) {
  return (
    <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        disabled={saving}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}
        {saving ? "Saving..." : edit ? "Update" : "Add"}
      </button>
    </div>
  );
}
